import { useCallback, useEffect, useRef, useState } from 'react';
import { CircleCheck, RefreshCw, Smartphone } from 'lucide-react';
import type { QrSignInStatus } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { MfaQrCode } from '@/components/MfaQrCode';
import { Button, Modal, Spinner } from '@/components/ui';

type Phase =
  | { kind: 'loading' }
  | { kind: 'showing'; code: string; link: string; expiresAt: number }
  | { kind: 'confirm'; code: string; deviceName: string; expiresAt: number }
  | { kind: 'done'; deviceName: string }
  | { kind: 'declined' }
  | { kind: 'error'; message: string };

const POLL_MS = 1500;

/**
 * Shows a QR code the DTS Mail iOS app scans to sign in to this account without a password or authenticator code.
 * Once a phone scans it, its name appears here and nothing happens until it is approved, so a photographed QR code
 * on its own can't sign anyone in. A code lasts 2 minutes and is replaced automatically while the window is open.
 */
export function PhoneSignInDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const live = useRef<string | null>(null); // the code currently on screen, cancelled when the window closes

  const start = useCallback(async () => {
    setPhase({ kind: 'loading' });
    try {
      const s = await api.qrSignInStart();
      live.current = s.code;
      setPhase({ kind: 'showing', code: s.code, link: s.link, expiresAt: Date.now() + s.expiresInSeconds * 1000 });
    } catch (e) {
      setPhase({ kind: 'error', message: errorMessage(e) });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void start();
    return () => {
      // Closing the window cancels an unscanned code and declines a phone still waiting.
      const code = live.current;
      live.current = null;
      if (code) void api.qrSignInDecide(code, false).catch(() => {});
    };
  }, [open, start]);

  // Countdown tick.
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  // Poll while a code is on screen or a phone waits for approval.
  const code = phase.kind === 'showing' || phase.kind === 'confirm' ? phase.code : null;
  useEffect(() => {
    if (!open || !code) return;
    let stopped = false;
    const tick = async () => {
      let s: QrSignInStatus;
      try {
        s = await api.qrSignInStatus(code);
      } catch {
        return; // transient; try again next tick
      }
      if (stopped || live.current !== code) return;
      if (s.status === 'claimed') {
        setPhase((p) => (p.kind === 'showing' ? { kind: 'confirm', code, deviceName: s.deviceName ?? 'A phone', expiresAt: Date.now() + s.expiresInSeconds * 1000 } : p));
      } else if (s.status === 'expired') {
        live.current = null;
        void start(); // a fresh code replaces the old one
      }
    };
    const t = setInterval(() => void tick(), POLL_MS);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [open, code, start]);

  const decide = async (approve: boolean) => {
    if (phase.kind !== 'confirm') return;
    setBusy(true);
    try {
      await api.qrSignInDecide(phase.code, approve);
      live.current = null;
      setPhase(approve ? { kind: 'done', deviceName: phase.deviceName } : { kind: 'declined' });
    } catch (e) {
      setPhase({ kind: 'error', message: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  const secondsLeft = phase.kind === 'showing' || phase.kind === 'confirm' ? Math.max(0, Math.round((phase.expiresAt - now) / 1000)) : 0;
  const clock = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return (
    <Modal open={open} onClose={onClose} title="Sign in on your phone" width={440}>
      <div className="flex flex-col items-center px-6 pb-6 pt-5 text-center">
        {phase.kind === 'loading' && (
          <div className="flex h-[216px] items-center justify-center">
            <Spinner className="size-6" />
          </div>
        )}

        {phase.kind === 'showing' && (
          <>
            <MfaQrCode value={phase.link} label="QR code for signing in to DTS Mail on your phone" />
            <ol className="mt-5 space-y-1.5 text-left text-[13px] text-muted">
              <li>1. Open DTS Mail on your iPhone and tap <span className="font-medium text-fg">Sign in with QR code</span>, or point the Camera app at the code.</li>
              <li>2. Approve the phone here when its name appears.</li>
            </ol>
            <div className="mt-4 text-[12px] text-faint">New code in {clock}</div>
          </>
        )}

        {phase.kind === 'confirm' && (
          <>
            <div className="flex size-14 items-center justify-center rounded-2xl bg-accent/15 text-accent">
              <Smartphone className="size-7" />
            </div>
            <div className="mt-4 text-[16px] font-semibold">Sign in on “{phase.deviceName}”?</div>
            <p className="mt-1.5 text-[13px] text-muted">
              This phone will get full access to your mailbox without a password or code. Only approve it if it's in your hand.
            </p>
            <div className="mt-5 flex gap-2">
              <Button variant="ghost" disabled={busy} onClick={() => void decide(false)}>Decline</Button>
              <Button variant="primary" loading={busy} onClick={() => void decide(true)} icon={<CircleCheck className="size-4" />}>Approve</Button>
            </div>
            <div className="mt-4 text-[12px] text-faint">Waiting {clock}</div>
          </>
        )}

        {phase.kind === 'done' && (
          <>
            <CircleCheck className="size-12 text-ok" />
            <div className="mt-3 text-[16px] font-semibold">“{phase.deviceName}” is signed in</div>
            <p className="mt-1.5 text-[13px] text-muted">It stays signed in until you sign out on the phone or change your password.</p>
            <Button className="mt-5" variant="primary" onClick={onClose}>Done</Button>
          </>
        )}

        {phase.kind === 'declined' && (
          <>
            <div className="text-[15px] font-semibold">Sign-in declined</div>
            <p className="mt-1.5 text-[13px] text-muted">The phone was not signed in.</p>
            <Button className="mt-5" variant="soft" onClick={() => void start()} icon={<RefreshCw className="size-4" />}>Show a new code</Button>
          </>
        )}

        {phase.kind === 'error' && (
          <>
            <div className="text-[13.5px] text-danger">{phase.message}</div>
            <Button className="mt-5" variant="soft" onClick={() => void start()} icon={<RefreshCw className="size-4" />}>Try again</Button>
          </>
        )}
      </div>
    </Modal>
  );
}
